import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stockApi } from './api';
import { XotskiFormBody, type NewXotski } from './AddXotskiDialog';
import { useT } from '@/lib/i18n/useT';
import { useCurrentBrand } from '@/lib/branding/useCurrentBrand';
import { useNavigate } from 'react-router-dom';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import type { XkStock } from '@/lib/api/types';

/**
 * Right-panel create/edit form for a brand's xotski (stock). Reuses the same
 * `XotskiFormBody` the modal uses; owns the create/update mutation + cache
 * invalidation so the FormPanelHost stays thin. Pass `stockId` + `initial`
 * to edit an existing row.
 */
export function XotskiForm({
  brandId,
  stockId,
  initial,
  onSaved,
  onCancel,
}: {
  brandId: number;
  /** Present → edit mode. */
  stockId?: number;
  initial?: Partial<NewXotski> | null;
  onSaved: (stock: XkStock) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const t = useT();
  const isEdit = stockId != null;
  const { brands, hasMultiple } = useCurrentBrand();
  const brandName = brands.find((b) => b.id === brandId)?.name ?? '';
  const destination = brandName ? { name: brandName, confirm: hasMultiple } : undefined;

  const save = useMutation({
    mutationFn: (v: NewXotski) =>
      isEdit
        ? stockApi.update(stockId!, v)
        : stockApi.create({ ...v, companyId: brandId || undefined }),
    onSuccess: (result, v) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
      toast({
        tone: 'success',
        title: isEdit ? `Xotski updated: "${v.name}"` : `Xotski created: "${v.name}"`,
        description: brandName ? t('stock.list.addedTo', { brand: brandName }) : undefined,
        action: { label: t('stock.list.seeInventory'), onClick: () => navigate('/brand-inventory') },
      });
      onSaved(result);
    },
    onError: (e: Error) =>
      toast({
        tone: 'error',
        title: isEdit ? 'Update failed' : 'Create failed',
        description: e.message,
      }),
  });

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto">
      <XotskiFormBody
        initial={initial}
        onSave={(v) => save.mutate(v)}
        onCancel={onCancel}
        loading={save.isPending}
        destination={destination}
      />
    </div>
  );
}
